// Companion/blocks/chatBlockPayload.ts
//
// `block.payload` is an opaque `Record<string, unknown>` (ora-be ADR-0006), so
// every field is read defensively here and nowhere else. A parser returns null
// when the block cannot be rendered at all — a missing content id — and
// degrades quietly otherwise, so a serve tool that grows a field never breaks a
// student's thread.
//
// This is also the seam that keeps the *new* protocol camelCase while the
// ported block bodies (`bodies/`) keep reading their original snake_case `item`
// shapes: nothing below the adapters in this directory knows both vocabularies.

import type { FlashcardScheduling } from '@/types/Flashcard';
import type {
  ChatArticleBlockPayload,
  ChatDashboardBlockPayload,
  ChatDashboardTopic,
  ChatDashboardTopicMetric,
  ChatDocumentBlockPayload,
  ChatDocumentExcerptBlockPayload,
  ChatDraftQuestionBlockExplanation,
  ChatDraftQuestionBlockOption,
  ChatDraftQuestionBlockPayload,
  ChatDraftQuestionReadyPayload,
  ChatFileBlockFormat,
  ChatFileBlockPayload,
  ChatFileBlockStatus,
  ChatNavigationBlockPayload,
  ChatFlashcardBlockCard,
  ChatFlashcardsBlockPayload,
  ChatHelpDocBlockPayload,
  ChatQuestionBlockLimits,
  ChatQuestionBlockOption,
  ChatQuestionBlockPayload,
  ChatSetResultBlockPayload,
  ChatStudySetBlockPayload,
  ChatStudySetFlashcard,
  ChatStudySetItem,
  ChatStudySetKind,
  ChatStudySetLaunch,
  ChatStudySetProgress,
  ChatStudySetStatus,
  ChatVideoBlockPayload,
} from '@/types/chatBlocks';

type Payload = Record<string, unknown>;

const asString = (value: unknown): string =>
  typeof value === 'string' ? value : '';

const asOptionalString = (value: unknown): string | null =>
  typeof value === 'string' && value !== '' ? value : null;

const asNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/**
 * The entries of an array that are actually objects. Exported because the
 * legacy restore reads arrays out of persisted UI state, which is just as
 * untrustworthy as a payload off the wire: one `null` in an options list would
 * otherwise throw during hydration and take a whole conversation with it.
 */
export const asRecordArray = (value: unknown): Payload[] =>
  Array.isArray(value)
    ? value.filter(
        (entry): entry is Payload =>
          typeof entry === 'object' && entry !== null && !Array.isArray(entry)
      )
    : [];

const asRecord = (value: unknown): Payload | null =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Payload)
    : null;

const parseQuestionLimits = (
  value: unknown
): ChatQuestionBlockLimits | null => {
  const record = asRecord(value);
  if (!record) return null;

  const remaining = asNumber(record.qbankRemaining);
  const limit = asNumber(record.qbankLimit);
  if (remaining === null && limit === null) return null;

  return { qbankRemaining: remaining, qbankLimit: limit };
};

const parseQuestionOptions = (value: unknown): ChatQuestionBlockOption[] =>
  asRecordArray(value)
    .map((option) => ({
      letter: asOptionalString(option.letter) ?? undefined,
      text: asString(option.text),
      explanation: asOptionalString(option.explanation),
      ...(typeof option.isCorrect === 'boolean'
        ? { isCorrect: option.isCorrect }
        : {}),
    }))
    // An option with nothing to read is not a choice a student can make.
    .filter((option) => option.text !== '');

export const parseQuestionBlockPayload = (
  payload: Payload
): ChatQuestionBlockPayload | null => {
  const vignetteId = asString(payload.vignetteId);
  if (!vignetteId) return null;

  return {
    vignetteId,
    variantId: asOptionalString(payload.variantId),
    stem: asString(payload.stem),
    options: parseQuestionOptions(payload.options),
    correctLetter: asOptionalString(payload.correctLetter),
    explanation: asOptionalString(payload.explanation),
    limits: parseQuestionLimits(payload.limits),
    selectedLetter: asOptionalString(payload.selectedLetter),
    lectureId: asOptionalString(payload.lectureId),
    // The stored marks (ORA-822). Kept absent when the server did not send
    // them, so a question served before they existed is not read as unmarked.
    ...('questionHighlights' in payload
      ? { questionHighlights: asOptionalString(payload.questionHighlights) }
      : {}),
    ...(Array.isArray(payload.answerCrossouts)
      ? {
          answerCrossouts: payload.answerCrossouts.map(
            (crossedOut) => crossedOut === true
          ),
        }
      : {}),
  };
};

/**
 * A draft's option letter, as the block compares it. The letter is the only key
 * joining an option to its explanation and to the student's pick, so every
 * letter the payload carries is read through here: a stray space or a
 * lower-case letter from the generator would otherwise silently un-match an
 * explanation, or mark a correct pick wrong.
 */
const asLetter = (value: unknown): string =>
  asString(value).trim().toUpperCase();

/** The entries whose letter is seen for the first time; the first one wins. */
const firstPerLetter = <T extends { letter: string }>(entries: T[]): T[] =>
  entries.filter(
    (entry, index) =>
      entries.findIndex((other) => other.letter === entry.letter) === index
  );

const parseDraftQuestionOptions = (
  value: unknown
): ChatDraftQuestionBlockOption[] =>
  firstPerLetter(
    asRecordArray(value)
      .map((option) => ({
        letter: asLetter(option.letter),
        text: asString(option.text),
      }))
      // A draft's letters are assigned by the tool after it shuffled the
      // options, so unlike a vetted question there is no positional fallback:
      // an option with no letter of its own cannot be matched to its
      // explanation. A repeated letter is dropped rather than drawn twice —
      // two rows answering to one letter would make the reveal ambiguous.
      .filter((option) => option.letter !== '' && option.text !== '')
  );

const parseDraftQuestionExplanations = (
  value: unknown
): ChatDraftQuestionBlockExplanation[] =>
  firstPerLetter(
    asRecordArray(value)
      .map((explanation) => ({
        letter: asLetter(explanation.letter),
        text: asString(explanation.text),
      }))
      .filter((explanation) => explanation.letter !== '')
  );

/** The `ready` arm: the question itself, with the rules the block relies on. */
const parseReadyDraftQuestion = (
  payload: Payload,
  draftId: string
): ChatDraftQuestionReadyPayload | null => {
  const options = parseDraftQuestionOptions(payload.options);
  // Unlike the vetted block, which can still show a stem with no choices, a
  // draft with no options is nothing: there is no library question behind it to
  // open instead, so it is drawn as the placeholder.
  if (options.length === 0) return null;

  const correctLetter = asLetter(payload.correctLetter);

  // The key is required, not optional. The reveal is driven entirely from this
  // payload, so a draft whose key names no option — or an ambiguous one — would
  // grade every pick wrong and tell the student so. The placeholder is the only
  // honest thing to draw instead.
  if (
    options.filter((option) => option.letter === correctLetter).length !== 1
  ) {
    return null;
  }

  const selectedLetter = asLetter(payload.selectedLetter);

  return {
    draftId,
    status: 'ready',
    sourceLabel: asString(payload.sourceLabel),
    questionType: asOptionalString(payload.questionType),
    stem: asString(payload.stem),
    options,
    correctLetter,
    explanations: parseDraftQuestionExplanations(payload.explanations),
    learningObjective: asString(payload.learningObjective),
    // A pick that names no option on screen is not a pick: it would reveal the
    // panel with nothing marked as the student's answer.
    selectedLetter: options.some((option) => option.letter === selectedLetter)
      ? selectedLetter
      : null,
    feedbackReason: asOptionalString(payload.feedbackReason),
  };
};

/**
 * A chat-authored draft, in whichever of its three states the server sent.
 *
 * `status` is the discriminator and it is required: a draft is served before
 * the question exists (the block asks the server to write it), so "which state
 * is this" is never a guess from which fields happen to be present.
 */
export const parseDraftQuestionBlockPayload = (
  payload: Payload
): ChatDraftQuestionBlockPayload | null => {
  const draftId = asString(payload.draftId);
  if (!draftId) return null;

  const sourceLabel = asString(payload.sourceLabel);

  if (payload.status === 'pending') {
    // The source label is what the waiting line names, and the focus is the
    // request itself; a pending draft missing either is not one this client can
    // honestly draw.
    const focus = asString(payload.focus);
    return sourceLabel && focus
      ? { draftId, status: 'pending', sourceLabel, focus }
      : null;
  }

  if (payload.status === 'failed') {
    const reason = asString(payload.reason);
    return sourceLabel && reason
      ? { draftId, status: 'failed', sourceLabel, reason }
      : null;
  }

  if (payload.status === 'ready')
    return parseReadyDraftQuestion(payload, draftId);

  return null;
};

const parseFlashcard = (card: Payload): ChatFlashcardBlockCard | null => {
  const id = asString(card.id);
  if (!id) return null;

  return {
    id,
    front: asString(card.front),
    back: asString(card.back),
    explanation: asOptionalString(card.explanation),
  };
};

export const parseFlashcardsBlockPayload = (
  payload: Payload
): ChatFlashcardsBlockPayload | null => {
  const cards = asRecordArray(payload.cards).flatMap((card) => {
    const parsed = parseFlashcard(card);
    return parsed ? [parsed] : [];
  });

  return cards.length > 0 ? { cards } : null;
};

export const parseVideoBlockPayload = (
  payload: Payload
): ChatVideoBlockPayload | null => {
  const videoId = asString(payload.videoId);
  if (!videoId) return null;

  return {
    videoId,
    title: asOptionalString(payload.title),
    description: asOptionalString(payload.description),
    thumbnailUrl: asOptionalString(payload.thumbnailUrl),
    startSecond: asNumber(payload.startSecond),
  };
};

export const parseArticleBlockPayload = (
  payload: Payload
): ChatArticleBlockPayload | null => {
  const articleId = asString(payload.articleId);
  if (!articleId) return null;

  return {
    articleId,
    title: asOptionalString(payload.title),
    sectionId: asOptionalString(payload.sectionId),
  };
};

export const parseHelpDocBlockPayload = (
  payload: Payload
): ChatHelpDocBlockPayload | null => {
  const slug = asString(payload.slug);
  if (!slug) return null;

  return {
    slug,
    title: asOptionalString(payload.title),
    content: asOptionalString(payload.content),
  };
};

const parseTopics = (value: unknown): ChatDashboardTopic[] =>
  asRecordArray(value)
    .map((topic) => ({
      name: asString(topic.name),
      accuracy: asNumber(topic.accuracy),
      // ORA-1101: only the readiness ranking's rows carry it.
      ...(topic.readiness !== undefined
        ? { readiness: asNumber(topic.readiness) }
        : {}),
    }))
    .filter((topic) => topic.name !== '');

const parseDashboardMetric = (
  value: unknown
): ChatDashboardTopicMetric | null =>
  value === 'readiness' || value === 'accuracy' ? value : null;

export const parseDashboardBlockPayload = (
  payload: Payload
): ChatDashboardBlockPayload | null => {
  const qbank = asRecord(payload.qbank);
  const metric = parseDashboardMetric(payload.metric);

  return {
    // Anything but an explicit global scope opens on the student's exam, which
    // is the scope the dashboard itself defaults to.
    view: payload.view === 'global' ? 'global' : 'exam',
    examName: asOptionalString(payload.examName),
    qbank: qbank
      ? {
          accuracy: asNumber(qbank.accuracy),
          completed: asNumber(qbank.completed),
          correct: asNumber(qbank.correct),
          recentAccuracy: asNumber(qbank.recentAccuracy),
          recentTotal: asNumber(qbank.recentTotal),
        }
      : null,
    weakTopics: parseTopics(payload.weakTopics),
    strongTopics: parseTopics(payload.strongTopics),
    ...(metric ? { metric } : {}),
  };
};

export const parseDocumentBlockPayload = (
  payload: Payload
): ChatDocumentBlockPayload | null => {
  const lectureId = asString(payload.lectureId);
  if (!lectureId) return null;

  return { lectureId, fileName: asOptionalString(payload.fileName) };
};

const FILE_FORMATS: readonly ChatFileBlockFormat[] = ['pdf', 'docx', 'csv'];

const FILE_STATUSES: readonly ChatFileBlockStatus[] = [
  'ready_to_build',
  'building',
  'ready',
  'failed',
];

export const parseFileBlockPayload = (
  payload: Payload
): ChatFileBlockPayload | null => {
  const exportId = asString(payload.exportId);
  const filename = asString(payload.filename);
  const format = asString(payload.format) as ChatFileBlockFormat;
  const status = asString(payload.status) as ChatFileBlockStatus;

  if (
    !exportId ||
    !filename ||
    !FILE_FORMATS.includes(format) ||
    !FILE_STATUSES.includes(status)
  ) {
    return null;
  }

  const itemCount = asNumber(payload.itemCount);

  return {
    exportId,
    filename,
    format,
    status,
    title: asOptionalString(payload.title),
    sourceLabel: asOptionalString(payload.sourceLabel),
    ...(itemCount !== null && Number.isSafeInteger(itemCount) && itemCount >= 0
      ? { itemCount }
      : {}),
    notes: Array.isArray(payload.notes)
      ? payload.notes.flatMap((note) => {
          const parsed = asOptionalString(note);
          return parsed ? [parsed] : [];
        })
      : [],
  };
};

export const parseDocumentExcerptBlockPayload = (
  payload: Payload
): ChatDocumentExcerptBlockPayload | null => {
  const page = asNumber(payload.page);

  // mcgraw-hill: a quoted chat-document page (`quote_reference`).
  if (payload.source === 'chat_document') {
    const documentId = asString(payload.documentId);
    const excerpt = asOptionalString(payload.excerpt);
    if (
      !documentId ||
      !excerpt ||
      page === null ||
      !Number.isSafeInteger(page) ||
      page < 1
    ) {
      return null;
    }

    return {
      source: 'chat_document',
      documentId,
      page,
      title: asOptionalString(payload.title),
      excerpt,
    };
  }

  const lectureId = asString(payload.lectureId);
  if (!lectureId) return null;

  return {
    lectureId,
    // A page is a count, so anything but a positive whole number is no page.
    page: page !== null && Number.isSafeInteger(page) && page > 0 ? page : null,
    title: asOptionalString(payload.title),
    excerpt: asOptionalString(payload.excerpt),
  };
};

export const parseNavigationBlockPayload = (
  payload: Record<string, unknown>
): ChatNavigationBlockPayload | null => {
  const label = asOptionalString(payload.label);
  if (!label) return null;

  return {
    label,
    ...(asOptionalString(payload.description) !== null
      ? { description: asOptionalString(payload.description) }
      : {}),
  };
};

const STUDY_SET_KINDS: readonly string[] = ['question', 'flashcard', 'video'];

const STUDY_SET_STATUSES: readonly string[] = [
  'served',
  'launched',
  'completed',
];

const STUDY_SET_LAUNCH_ROUTES: readonly string[] = [
  'study.qbank.block',
  'study.flashcards',
  'study.video',
  'study.qbank.review',
];

/** A count off the wire: a whole number of items, never negative. */
const asCount = (value: unknown): number | null => {
  const parsed = asNumber(value);
  return parsed !== null && Number.isSafeInteger(parsed) && parsed >= 0
    ? parsed
    : null;
};

const parseStudySetProgress = (
  value: unknown,
  total: number
): ChatStudySetProgress => {
  const record = asRecord(value);
  const done = asCount(record?.done) ?? 0;
  const parsedTotal = asCount(record?.total) ?? total;
  const correct = asCount(record?.correct);

  return {
    // A `done` past the total would draw "4 of 3 done"; the total is the one
    // the set was built with, so it wins.
    done: Math.min(done, parsedTotal),
    total: parsedTotal,
    ...(correct === null ? {} : { correct }),
  };
};

/**
 * The scheduling the deck rates against. Every field is required for the FSRS
 * step, so a partial object is no scheduling at all — the card is then treated
 * as new, which is what a null means on the wire.
 */
const parseFlashcardScheduling = (
  value: unknown
): FlashcardScheduling | null => {
  const record = asRecord(value);
  if (!record) return null;

  const due = asOptionalString(record.due);
  const lastReview = asOptionalString(record.lastReview);
  const stability = asNumber(record.stability);
  const difficulty = asNumber(record.difficulty);
  const elapsedDays = asNumber(record.elapsedDays);
  const scheduledDays = asNumber(record.scheduledDays);
  const reps = asNumber(record.reps);
  const lapses = asNumber(record.lapses);
  const state = asNumber(record.state);
  const difficultyIndex = asNumber(record.difficultyIndex);

  if (
    due === null ||
    lastReview === null ||
    stability === null ||
    difficulty === null ||
    elapsedDays === null ||
    scheduledDays === null ||
    reps === null ||
    lapses === null ||
    state === null ||
    difficultyIndex === null
  ) {
    return null;
  }

  return {
    due,
    lastReview,
    stability,
    difficulty,
    elapsedDays,
    scheduledDays,
    reps,
    lapses,
    state,
    difficultyIndex,
  };
};

const parseStudySetFlashcard = (
  value: unknown
): ChatStudySetFlashcard | null => {
  const record = asRecord(value);
  if (!record) return null;

  const card = parseFlashcard(record);
  if (!card) return null;

  return {
    ...card,
    variantId: asString(record.variantId),
    // A card always has the variant it was presented as, so a missing or
    // unusable count is one variant rather than none.
    variantCount: Math.max(asCount(record.variantCount) ?? 1, 1),
    scheduling: parseFlashcardScheduling(record.scheduling),
  };
};

/** A wire percent the rail can draw a bar from, whatever the server sent. */
const clampPercent = (value: number): number =>
  Math.min(100, Math.max(0, value));

const parseStudySetItem = (item: Payload): ChatStudySetItem | null => {
  const ord = asCount(item.ord) ?? 0;
  const done = item.done === true;

  if (item.kind === 'question') {
    const question = parseQuestionBlockPayload(asRecord(item.question) ?? {});
    return question ? { ord, done, kind: 'question', question } : null;
  }

  if (item.kind === 'flashcard') {
    const card = parseStudySetFlashcard(item.card);
    return card ? { ord, done, kind: 'flashcard', card } : null;
  }

  if (item.kind === 'video') {
    const record = asRecord(item.video) ?? {};
    const video = parseVideoBlockPayload(record);
    return video
      ? {
          ord,
          done,
          kind: 'video',
          video: {
            ...video,
            durationSeconds: asNumber(record.durationSeconds),
            // A server that does not send progress still gets the rail right
            // for the item it already called done.
            progressPercent: clampPercent(
              asNumber(record.progressPercent) ?? (done ? 100 : 0)
            ),
          },
        }
      : null;
  }

  return null;
};

/**
 * The items of an inline set, or undefined for a lazy one. The distinction is
 * load-bearing: `items: []` would draw an empty deck, while a set with no
 * `items` key at all is one whose whole content lives behind the footer button.
 */
const parseStudySetItems = (
  value: unknown,
  kind: ChatStudySetKind
): ChatStudySetItem[] | undefined => {
  if (!Array.isArray(value)) return undefined;

  const items = asRecordArray(value)
    .flatMap((item) => {
      const parsed = parseStudySetItem(item);
      return parsed ? [parsed] : [];
    })
    // An item of another kind in a set is a malformed payload, not a mixed
    // deck: the renderer draws one kind.
    .filter((item) => item.kind === kind)
    .sort((left, right) => left.ord - right.ord);

  return items.length > 0 ? items : undefined;
};

const parseStudySetLaunch = (
  value: unknown,
  setId: string
): ChatStudySetLaunch | null => {
  const record = asRecord(value);
  const route = asString(record?.route);
  if (!STUDY_SET_LAUNCH_ROUTES.includes(route)) return null;

  const params = asRecord(record?.params);
  const blockId = asOptionalString(params?.blockId);

  return {
    route: route as ChatStudySetLaunch['route'],
    params: { setId, ...(blockId ? { blockId } : {}) },
  };
};

export const parseStudySetBlockPayload = (
  payload: Payload
): ChatStudySetBlockPayload | null => {
  const setId = asString(payload.setId);
  if (!setId) return null;

  const kind = asString(payload.kind);
  if (!STUDY_SET_KINDS.includes(kind)) return null;

  const launch = parseStudySetLaunch(payload.launch, setId);
  if (!launch) return null;

  const status = asString(payload.status);
  const items = parseStudySetItems(payload.items, kind as ChatStudySetKind);
  const requestedCount = asCount(payload.requestedCount) ?? items?.length ?? 0;
  const moreSession = asRecord(payload.moreSession);
  const moreSessionCount = asCount(moreSession?.count);

  return {
    setId,
    kind: kind as ChatStudySetKind,
    status: (STUDY_SET_STATUSES.includes(status)
      ? status
      : 'served') as ChatStudySetStatus,
    requestedCount,
    poolCount: asCount(payload.poolCount),
    inlineMax: asCount(payload.inlineMax) ?? 0,
    scopeLabel: asString(payload.scopeLabel),
    progress: parseStudySetProgress(payload.progress, requestedCount),
    ...(items ? { items } : {}),
    limits: parseQuestionLimits(payload.limits),
    launch,
    ...(moreSessionCount !== null && moreSessionCount > 0
      ? { moreSession: { count: moreSessionCount } }
      : {}),
  };
};

export const parseSetResultBlockPayload = (
  payload: Payload
): ChatSetResultBlockPayload | null => {
  const setId = asString(payload.setId);
  if (!setId) return null;

  const kind = asString(payload.kind);
  if (!STUDY_SET_KINDS.includes(kind)) return null;

  const status = asString(payload.status);

  return {
    setId,
    kind: kind as ChatStudySetKind,
    status: (STUDY_SET_STATUSES.includes(status)
      ? status
      : 'launched') as ChatStudySetStatus,
    progress: parseStudySetProgress(payload.progress, 0),
    scopeLabel: asString(payload.scopeLabel),
  };
};
